import type { FileInfo } from '@puppycad/proto'
import {
  type AssemblyModelAsset,
  hydrateImportedPartAssetBrepRuntime as hydrateImportedPartAssetBrepRuntimeInCadCore,
  hydrateImportedTopologySourceBrepRuntime as hydrateImportedTopologySourceBrepRuntimeInCadCore,
  toImportedBrepHydrationError,
  type BRepRegistrar,
  type ImportedTopologySource,
  type PartAsset,
} from '@puppycad/cad-core'
import { downloadCadFile } from '@/services/api/cad-session'
import { logger } from '@/utils'
import {
  isImportedModelLoadAbortedError,
  throwIfImportedModelRequestStale,
  type ImportedModelLoadGuard,
} from './imported-model-load-guard'
import {
  type ImportedBrepRuntimeCache,
} from './imported-model-brep-runtime-cache'
import type { ImportedModelFileBytesReader } from './imported-model-loader'

/**
 * 读取导入模型相关文件字节。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 需要读取的文件。
 * @param readFileBytes 可选内存 reader。
 * @returns 文件字节。
 */
function readImportedModelFileBytes(
  sessionId: string,
  file: FileInfo,
  readFileBytes?: ImportedModelFileBytesReader,
): Promise<Uint8Array> {
  if (readFileBytes) {
    return readFileBytes(file)
  }

  return downloadCadFile(
    sessionId,
    file.fileId,
    typeof file.uuid === 'string' ? file.uuid : '',
  )
}

/**
 * 为零件资产补齐 sidecar BRep 运行时拓扑。
 *
 * 设计说明：
 * 1. web-client 负责 sidecar 下载与会话级缓存，cad-core 负责 topo 资产与 BRep Shape 的一致性提交；
 * 2. `ImportedBrepRuntimeCache` 在会话维度缓存 sidecar 文档与解析结果，从底层收口重复请求；
 * 3. 任一声明的可见 body 读取或注册失败时透传严格错误，由上层 staged Worker/场景事务统一回滚。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 当前 GLB 文件。
 * @param asset 已解析的零件资产。
 * @param options 运行时依赖。
 */
export async function hydrateImportedPartAssetBrepRuntime(
  sessionId: string,
  file: FileInfo,
  asset: PartAsset,
  options: {
    projectFiles: FileInfo[]
    importedBrepRuntimeCache?: ImportedBrepRuntimeCache
    brepRegistrar?: BRepRegistrar
    guard?: ImportedModelLoadGuard
    readFileBytes?: ImportedModelFileBytesReader
  },
): Promise<void> {
  try {
    await hydrateImportedPartAssetBrepRuntimeInCadCore(file, asset, {
      projectFiles: options.projectFiles,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      brepRegistrar: options.brepRegistrar,
      readFileBytes: async (sidecarFile) => readImportedModelFileBytes(
        sessionId,
        sidecarFile,
        options.readFileBytes,
      ),
      assertCurrent: () => {
        throwIfImportedModelRequestStale(options.guard)
      },
      logger: {
        onWarn: (message) => {
          logger.warn(`[imported-model-brep-loader] ${message}`)
        },
        onError: (message, error) => {
          logger.error(`[imported-model-brep-loader] ${message}`, error)
        },
      },
    })
  } catch (error) {
    if (isImportedModelLoadAbortedError(error)) {
      throw error
    }
    throw toImportedBrepHydrationError(error)
  }
}

/**
 * 为任意导入拓扑源补齐 sidecar BRep 运行时拓扑。
 *
 * 设计说明：
 * 1. 零件与装配自身局部几何都只依赖 `ImportedTopologySource` 这一层约定；
 * 2. 这样 web-client 可以统一处理 sidecar 下载、去重缓存与 worker 网格化，不再把 assembly 局部几何排除在外；
 * 3. 实际 three.js runtime 写回依旧交给 cad-core 的 `applyImportedModelTopologyRuntime`，保持分层边界清晰。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 当前 GLB 文件。
 * @param topologySource 需要补齐 BRep 运行时拓扑的导入拓扑源。
 * @param options 运行时依赖。
 */
export async function hydrateImportedTopologySourceBrepRuntime(
  sessionId: string,
  file: FileInfo,
  topologySource: ImportedTopologySource,
  options: {
    projectFiles: FileInfo[]
    importedBrepRuntimeCache?: ImportedBrepRuntimeCache
    brepRegistrar?: BRepRegistrar
    guard?: ImportedModelLoadGuard
    readFileBytes?: ImportedModelFileBytesReader
  },
): Promise<void> {
  try {
    await hydrateImportedTopologySourceBrepRuntimeInCadCore(file, topologySource, {
      projectFiles: options.projectFiles,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      brepRegistrar: options.brepRegistrar,
      readFileBytes: async (sidecarFile) => readImportedModelFileBytes(
        sessionId,
        sidecarFile,
        options.readFileBytes,
      ),
      assertCurrent: () => {
        throwIfImportedModelRequestStale(options.guard)
      },
      logger: {
        onWarn: (message) => {
          logger.warn(`[imported-model-brep-loader] ${message}`)
        },
        onError: (message, error) => {
          logger.error(`[imported-model-brep-loader] ${message}`, error)
        },
      },
    })
  } catch (error) {
    if (isImportedModelLoadAbortedError(error)) {
      throw error
    }
    throw toImportedBrepHydrationError(error)
  }
}

/**
 * 为装配资产自身的局部几何补齐 sidecar BRep 运行时拓扑。
 *
 * 设计说明：
 * 1. `AssemblyModelAsset` 的局部几何统一收口到 `localTopologySource`，无论来源是 embedded、inline 还是 referenced；
 * 2. 这里提供装配级门面，是为了避免上层 loader 再按来源分散编写 `if (asset.localTopologySource)` 分支；
 * 3. 若当前装配没有局部几何，则直接返回，保持“引用子文件”和“装配自身局部几何”两条职责边界清晰。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 当前 GLB 文件。
 * @param asset 已解析的装配资产。
 * @param options 运行时依赖。
 */
export async function hydrateImportedAssemblyAssetBrepRuntime(
  sessionId: string,
  file: FileInfo,
  asset: Pick<AssemblyModelAsset, 'localTopologySource'>,
  options: {
    projectFiles: FileInfo[]
    importedBrepRuntimeCache?: ImportedBrepRuntimeCache
    brepRegistrar?: BRepRegistrar
    guard?: ImportedModelLoadGuard
    readFileBytes?: ImportedModelFileBytesReader
  },
): Promise<void> {
  if (!asset.localTopologySource) {
    return
  }

  await hydrateImportedTopologySourceBrepRuntime(
    sessionId,
    file,
    asset.localTopologySource,
    options,
  )
}
